import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { Toaster } from "@/components/ui/sonner";
import { Loader2 } from "lucide-react";

import PublicLayout from "@/components/public/PublicLayout";
import Home from "@/pages/public/Home";
import Nosotros from "@/pages/public/Nosotros";
import Beneficios from "@/pages/public/Beneficios";
import Membresias from "@/pages/public/Membresias";
import Capacitacion from "@/pages/public/Capacitacion";
import CursoMicrositio from "@/pages/public/CursoMicrositio";
import Noticias from "@/pages/public/Noticias";
import NoticiaDetalle from "@/pages/public/NoticiaDetalle";
import Contacto from "@/pages/public/Contacto";
import HagaseSocio from "@/pages/public/HagaseSocio";
import PublicCard from "@/pages/public/PublicCard";
import EventosList from "@/pages/public/EventosList";
import EventoMicrositio from "@/pages/public/EventoMicrositio";
import MicrositioSocio from "@/pages/public/MicrositioSocio";
import Propiedades from "@/pages/public/Propiedades";
import PropiedadDetalle from "@/pages/public/PropiedadDetalle";
import Mapa from "@/pages/public/Mapa";
import AuthPage from "@/pages/auth/AuthPage";
import AuthCallback from "@/pages/auth/AuthCallback";
import RegistroPendiente from "@/pages/auth/RegistroPendiente";
import AppLayout from "@/pages/app/AppLayout";
import MemberDashboard from "@/pages/app/MemberDashboard";
import MemberFeed from "@/pages/app/MemberFeed";
import MemberMap from "@/pages/app/MemberMap";
import MemberPostDetail from "@/pages/app/MemberPostDetail";
import MemberCredential from "@/pages/app/MemberCredential";
import MemberProfile from "@/pages/app/MemberProfile";
import MemberProperties from "@/pages/app/MemberProperties";
import MemberDues from "@/pages/app/MemberDues";
import MemberBenefits from "@/pages/app/MemberBenefits";
import MemberDirectory from "@/pages/app/MemberDirectory";
import MemberEvents from "@/pages/app/MemberEvents";
import ComingSoonPage from "@/components/ComingSoonPage";
import { LineChart, Handshake, FileText, MessageSquare, Bot, GraduationCap } from "lucide-react";
import AdminLayout from "@/pages/admin/AdminLayout";
import AdminDashboard from "@/pages/admin/AdminDashboard";
import AdminMembers from "@/pages/admin/AdminMembers";
import AdminRequests from "@/pages/admin/AdminRequests";
import AdminUsers from "@/pages/admin/AdminUsers";
import AdminMessages from "@/pages/admin/AdminMessages";
import AdminNews from "@/pages/admin/AdminNews";
import AdminProperties from "@/pages/admin/AdminProperties";
import AdminBilling from "@/pages/admin/AdminBilling";
import AdminBroadcast from "@/pages/admin/AdminBroadcast";
import AdminHomeConfig from "@/pages/admin/AdminHomeConfig";
import AdminAnalytics from "@/pages/admin/AdminAnalytics";
import AdminEvents from "@/pages/admin/AdminEvents";
import AdminTrainings from "@/pages/admin/AdminTrainings";
import AdminPartners from "@/pages/admin/AdminPartners";
import AdminBenefits from "@/pages/admin/AdminBenefits";
import AdminAbout from "@/pages/admin/AdminAbout";

function FullScreenLoader() {
  return (
    <div className="flex min-h-screen items-center justify-center bg-cnib-navy" data-testid="app-loading">
      <Loader2 className="h-10 w-10 animate-spin text-orange-500" />
    </div>
  );
}

function MembersOnlyRoute({ children }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  return children;
}

function ProtectedRoute({ children, roles }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return <FullScreenLoader />;
  if (!user) return <Navigate to="/login" state={{ from: location }} replace />;
  if (roles && !roles.includes(user.role)) {
    const home = user.role === "admin" || user.role === "superadmin" ? "/admin" : "/app";
    return <Navigate to={home} replace />;
  }
  return children;
}

function AppRouter() {
  const location = useLocation();
  // Detect OAuth session_id synchronously during render (prevents race conditions)
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  // Detect microsite subdomain, e.g. maria-gonzales.cnib.bo o {slug}-evento.cnib.bo
  if (typeof window !== 'undefined') {
    const host = window.location.hostname;
    const parts = host.split('.');
    const EXCLUDED = new Set(['www', 'cnib', 'app', 'api', 'admin', 'cni-inmobiliaria']);
    if (parts.length >= 3 && !EXCLUDED.has(parts[0]) && !host.startsWith('localhost')) {
      const sub = parts[0];
      // Convention: event subdomains end with "-evento" (e.g. congreso-cnib-2026-evento.cnib.bo)
      if (sub.endsWith('-evento')) {
        const slug = sub.replace(/-evento$/, '');
        return (
          <Routes>
            <Route path="*" element={<EventoMicrositio key={slug} />} />
          </Routes>
        );
      }
      return (
        <Routes>
          <Route path="*" element={<MicrositioSocio slug={sub} />} />
        </Routes>
      );
    }
  }
  return (
    <Routes>
      <Route element={<PublicLayout />}>
        <Route path="/" element={<Home />} />
        <Route path="/nosotros" element={<Nosotros />} />
        <Route path="/beneficios" element={<Beneficios />} />
        <Route path="/membresias" element={<Membresias />} />
        <Route path="/capacitacion" element={<Capacitacion />} />
        <Route path="/capacitacion/:slug" element={<CursoMicrositio />} />
        <Route path="/noticias" element={<Noticias />} />
        <Route path="/noticias/:slug" element={<NoticiaDetalle />} />
        <Route path="/eventos" element={<EventosList />} />
        <Route path="/eventos/:slug" element={<EventoMicrositio />} />
        <Route path="/contacto" element={<Contacto />} />
        <Route path="/hagase-socio" element={<HagaseSocio />} />
        <Route path="/propiedades" element={<MembersOnlyRoute><Propiedades /></MembersOnlyRoute>} />
        <Route path="/propiedades/:id" element={<PropiedadDetalle />} />
        <Route path="/mapa" element={<MembersOnlyRoute><Mapa /></MembersOnlyRoute>} />
        <Route path="/p/:token" element={<PublicCard />} />
      </Route>
      <Route path="/socio/:slug" element={<MicrositioSocio />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/registro" element={<AuthPage mode="register" />} />
      <Route path="/registro/pendiente" element={<RegistroPendiente />} />
      <Route
        path="/app"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<MemberDashboard />} />
        <Route path="feed" element={<MemberFeed />} />
        <Route path="feed/:postId" element={<MemberPostDetail />} />
        <Route path="mapa" element={<MemberMap />} />
        <Route path="credencial" element={<MemberCredential />} />
        <Route path="perfil" element={<MemberProfile />} />
        <Route path="propiedades" element={<MemberProperties />} />
        <Route path="cuotas" element={<MemberDues />} />
        <Route path="beneficios" element={<MemberBenefits />} />
        <Route path="directorio" element={<MemberDirectory />} />
        <Route path="eventos" element={<MemberEvents />} />
        <Route path="chat" element={
          <ComingSoonPage
            title="Chat IA Inmobiliario"
            description="Asistente inteligente entrenado en leyes, normativas y mejores prácticas inmobiliarias bolivianas."
            icon={Bot}
            color="bg-fuchsia-500"
            features={[
              { title: 'Consultas legales', text: 'Respuestas apoyadas en normativa vigente.' },
              { title: 'Streaming en vivo', text: 'Respuestas rápidas con tecnología GPT.' },
            ]}
          />
        } />
        <Route path="campus" element={
          <ComingSoonPage
            title="Campus Virtual"
            description="Cursos, certificaciones y capacitaciones exclusivas para socios CNIB."
            icon={GraduationCap}
            color="bg-teal-500"
          />
        } />
        <Route path="observatorio" element={
          <ComingSoonPage
            title="Observatorio Inmobiliario"
            description="Indicadores, reportes y análisis en tiempo real del mercado boliviano."
            icon={LineChart}
            color="bg-emerald-500"
          />
        } />
        <Route path="oportunidades" element={
          <ComingSoonPage
            title="Oportunidades"
            description="Bolsa de negocios, inversionistas y colaboraciones entre socios."
            icon={Handshake}
            color="bg-amber-500"
          />
        } />
        <Route path="biblioteca" element={
          <ComingSoonPage
            title="Biblioteca Legal"
            description="Contratos, formatos y documentos listos para usar."
            icon={FileText}
            color="bg-slate-700"
          />
        } />
        <Route path="mensajeria" element={
          <ComingSoonPage
            title="Mensajería"
            description="Chat directo entre socios para coordinar negocios y referidos."
            icon={MessageSquare}
            color="bg-rose-500"
          />
        } />
      </Route>
      <Route
        path="/admin"
        element={
          <ProtectedRoute roles={["admin", "superadmin"]}>
            <AdminLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<AdminDashboard />} />
        <Route path="socios" element={<AdminMembers />} />
        <Route path="usuarios" element={<AdminUsers />} />
        <Route path="solicitudes" element={<AdminRequests />} />
        <Route path="mensajes" element={<AdminMessages />} />
        <Route path="noticias" element={<AdminNews />} />
        <Route path="propiedades" element={<AdminProperties />} />
        <Route path="facturacion" element={<AdminBilling />} />
        <Route path="notificaciones" element={<AdminBroadcast />} />
        <Route path="home" element={<AdminHomeConfig />} />
        <Route path="analytics" element={<AdminAnalytics />} />
        <Route path="eventos" element={<AdminEvents />} />
        <Route path="capacitaciones" element={<AdminTrainings />} />
        <Route path="partners" element={<AdminPartners />} />
        <Route path="beneficios" element={<AdminBenefits />} />
        <Route path="nosotros" element={<AdminAbout />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster position="top-right" richColors />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
